import { supabase } from "./supabase";

/* ───────────────── Tiny TTL cache ─────────────────
   Module-level memoisation so route navigation feels snappy.
   Returns the stale value immediately for any caller that
   passes `swr: true` and kicks off a refresh in the background.
*/

type CacheEntry<T> = { value: T; ts: number; inflight: Promise<T> | null };
const _cache = new Map<string, CacheEntry<any>>();
const DEFAULT_TTL_MS = 30_000;

function getCache<T>(key: string): CacheEntry<T> | undefined { return _cache.get(key); }

async function cached<T>(key: string, ttlMs: number, fetcher: () => Promise<T>, opts?: { swr?: boolean }): Promise<T> {
  const now = Date.now();
  const hit = getCache<T>(key);
  if (hit && (now - hit.ts) < ttlMs) return hit.value;

  // SWR: return stale immediately, refresh in background.
  if (hit && opts?.swr) {
    if (!hit.inflight) {
      hit.inflight = fetcher()
        .then(v => { _cache.set(key, { value: v, ts: Date.now(), inflight: null }); return v; })
        .catch(e => { hit.inflight = null; throw e; });
    }
    return hit.value;
  }

  // No cache or expired without SWR — await fresh fetch.
  if (hit?.inflight) return hit.inflight;
  const p = fetcher().then(v => { _cache.set(key, { value: v, ts: Date.now(), inflight: null }); return v; });
  _cache.set(key, { value: hit?.value, ts: hit?.ts || 0, inflight: p } as any);
  return p;
}

/** Invalidate every cache entry — call after a mode submits + total_points changes. */
export function invalidateAllCaches() { _cache.clear(); }
if (typeof window !== "undefined") {
  // After a session ends, the points-updated event fires — bust the cache so
  // the next leaderboard / mastery read is fresh.
  window.addEventListener("arabise:points-updated", () => invalidateAllCaches());
}

export type LeaderboardRow = {
  id: string;
  full_name: string;
  avatar_url: string | null;
  total_points: number;
  streak: number;
};

export type Topic = {
  id: string;
  name_en: string;
  name_ar: string;
};

export type VocabWord = {
  id: number | string;
  arabic: string;
  english: string;
  transliteration: string;
  topic: string;
};

export type SentenceRow = {
  id: number | string;
  topic: string;
  arabic: string;
  english: string;
  display_order: number | null;
};

const RECENT_TOPIC_KEY = "arabise-recent-topic";

/** Top N players via the get_leaderboard SECURITY DEFINER RPC. Cached 30s, SWR. */
export async function fetchLeaderboard(limit = 50): Promise<LeaderboardRow[]> {
  return cached(`leaderboard:${limit}`, DEFAULT_TTL_MS, async () => {
    const { data, error } = await supabase.rpc("get_leaderboard", { p_limit: limit });
    if (error) { console.warn("[queries] get_leaderboard failed", error); return []; }
    return (data || []).map((r: any) => ({
      id:           r.id,
      full_name:    r.full_name || "Anonymous",
      avatar_url:   r.avatar_url || null,
      total_points: Number(r.total_points) || 0,
      streak:       Number(r.streak) || 0
    }));
  }, { swr: true });
}

export async function updateProfile(patch: { full_name?: string; avatar_url?: string }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in.");
  const { error } = await supabase
    .from("profiles")
    .update(patch)
    .eq("id", user.id);
  if (error) throw error;
}

/** What the Supabase Storage default config accepts for images. */
export const AVATAR_MIME_TYPES = [
  "image/png", "image/jpeg", "image/webp", "image/gif", "image/avif"
];
export const AVATAR_ACCEPT = AVATAR_MIME_TYPES.join(",");
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;   // 5 MB — matches Supabase default

/**
 * Upload an image to the "avatars" bucket as <uid>/<timestamp>.<ext>,
 * then return the public URL. Validates MIME + size first.
 */
export async function uploadAvatar(file: File): Promise<string> {
  if (!AVATAR_MIME_TYPES.includes(file.type)) {
    throw new Error("Pick a PNG, JPG, WEBP, GIF or AVIF image.");
  }
  if (file.size > AVATAR_MAX_BYTES) {
    throw new Error(`Image must be under ${(AVATAR_MAX_BYTES / 1024 / 1024).toFixed(0)} MB.`);
  }
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in.");

  const ext = (file.name.split(".").pop() || file.type.split("/").pop() || "png").toLowerCase();
  const path = `${user.id}/${Date.now()}.${ext}`;

  const { error: upErr } = await supabase.storage
    .from("avatars")
    .upload(path, file, {
      contentType: file.type,
      cacheControl: "3600",
      upsert:      false
    });
  if (upErr) {
    if (/bucket .* not found/i.test(upErr.message)) {
      throw new Error(
        "Create an \"avatars\" bucket in Supabase Storage first " +
        "(Dashboard → Storage → New bucket → public)."
      );
    }
    throw upErr;
  }

  const { data: { publicUrl } } = supabase.storage.from("avatars").getPublicUrl(path);
  return publicUrl;
}

/* ───────────────── Topics + vocab + sentences ───────────────── */

export async function fetchTopics(): Promise<Topic[]> {
  // Topics rarely change — cache 10 minutes, SWR.
  return cached("topics", 600_000, async () => {
    const tryOrder = await supabase
      .from("topics")
      .select("id, name_en, name_ar")
      .order("display_order", { ascending: true, nullsFirst: false })
      .order("name_en",       { ascending: true });
    if (!tryOrder.error) return tryOrder.data || [];

    console.warn("[queries] topics display_order failed, falling back", tryOrder.error);
    const fb = await supabase
      .from("topics")
      .select("id, name_en, name_ar")
      .order("name_en", { ascending: true });
    if (fb.error) { console.warn("[queries] topics fallback", fb.error); return []; }
    return fb.data || [];
  }, { swr: true });
}

export async function fetchTopicVocab(topicId: string): Promise<VocabWord[]> {
  const { data, error } = await supabase
    .from("vocab")
    .select("id, arabic, english, transliteration, topic")
    .eq("topic", topicId);
  if (error) { console.warn("[queries] vocab", error); return []; }
  return data || [];
}

export async function fetchTopicInfo(topicId: string): Promise<Topic | null> {
  const { data, error } = await supabase
    .from("topics")
    .select("id, name_en, name_ar")
    .eq("id", topicId)
    .maybeSingle();
  if (error) { console.warn("[queries] topic info", error); return null; }
  return data;
}

export async function fetchSentences(topicId: string | null): Promise<SentenceRow[]> {
  let q = supabase.from("sentences").select("id, topic, arabic, english, display_order");
  if (topicId) q = q.eq("topic", topicId);
  const { data, error } = await q;
  if (error) { console.warn("[queries] sentences", error); return []; }
  return data || [];
}

/* ───────────────── Progress + points ───────────────── */

/**
 * Insert one progress row. Matches the legacy js/quiz-shared.saveProgress
 * signature: word_id is stored as TEXT for cross-mode tolerance
 * ("s:42" from sentence-builder coexists with numeric vocab ids).
 *
 * Silent on missing columns (pre-v2 schemas) — falls back to a base insert.
 */
export async function saveProgress(opts: {
  word_id: string | number;
  correct: boolean;
  mode:    string;
  points?: number;
  assisted?: boolean;
  timeMs?:  number;
}): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  const base: Record<string, any> = {
    user_id:     user.id,
    word_id:     String(opts.word_id),
    correct:     !!opts.correct,
    answered_at: new Date().toISOString()
  };
  const full: Record<string, any> = {
    ...base,
    points:        Math.max(0, Math.round(opts.points || 0)),
    assisted:      !!opts.assisted,
    time_taken_ms: opts.timeMs != null ? Math.round(opts.timeMs) : null,
    mode:          opts.mode
  };

  try {
    const { error } = await supabase.from("progress").insert([full]);
    if (!error) return;
    const msg = (error.message || "") + " " + ((error as any).details || "");
    if (/column .* does not exist/i.test(msg) || /could not find the .* column/i.test(msg)) {
      console.warn("[queries] progress: missing columns — falling back to base insert", error);
      const { error: e2 } = await supabase.from("progress").insert([base]);
      if (e2) console.warn("[queries] base progress insert failed:", e2);
    } else {
      console.warn("[queries] progress save failed", error);
    }
  } catch (e) { console.warn("[queries] progress save threw", e); }
}

/** End-of-round point award. Fires arabise:points-updated so the top bar pill re-fetches. */
export async function submitQuizSession(opts: {
  claimed_correct: number;
  session_started: Date | string;
  mode: string;
}): Promise<{ new_total: number; points_awarded: number } | null> {
  const started = opts.session_started instanceof Date
    ? opts.session_started.toISOString()
    : opts.session_started;
  const { data, error } = await supabase.rpc("submit_quiz_session", {
    p_claimed_correct: opts.claimed_correct,
    p_session_started: started,
    p_mode:            opts.mode
  });
  if (error) { console.warn("[queries] submit_quiz_session", error); return null; }
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  window.dispatchEvent(new CustomEvent("arabise:points-updated", { detail: row }));
  return {
    new_total:      Number(row.new_total)      || 0,
    points_awarded: Number(row.points_awarded) || 0
  };
}

/* ───────────────── Mastery + topic progress ───────────────── */

export type TopicMastery = {
  topicId: string;
  attempted: number;
  correct:   number;
  total:     number;
  pct:       number;       // 0..100 (correct / total)
  done:      boolean;      // pct >= 80
};

/**
 * For the current user: across ALL their progress rows, how many
 * distinct word_ids are correct, and how does that map onto each
 * topic's vocab? Returns Map keyed by topic id.
 *
 * Uses one progress query + one vocab query so it scales.
 */
export async function fetchTopicMastery(): Promise<Map<string, TopicMastery>> {
  // Per-user, so include the user id in the cache key.
  const { data: { user } } = await supabase.auth.getUser();
  const out = new Map<string, TopicMastery>();
  if (!user) return out;

  return cached(`mastery:${user.id}`, DEFAULT_TTL_MS, async () => {
    const [topics, vocabRes, progRes] = await Promise.all([
      fetchTopics(),
      supabase.from("vocab").select("id, topic"),
      supabase.from("progress").select("word_id, correct").eq("user_id", user.id)
    ]);

    if (vocabRes.error) console.warn("[queries] mastery vocab", vocabRes.error);
    if (progRes.error)  console.warn("[queries] mastery progress", progRes.error);

    const vocab: { id: string | number; topic: string }[] = vocabRes.data || [];
    const progress: { word_id: string; correct: boolean }[] = progRes.data || [];

    const correctSet = new Set<string>();
    progress.forEach(r => { if (r.correct) correctSet.add(String(r.word_id)); });

    const byTopic = new Map<string, { id: string; correct: boolean }[]>();
    vocab.forEach(w => {
      const arr = byTopic.get(w.topic) || [];
      arr.push({ id: String(w.id), correct: correctSet.has(String(w.id)) });
      byTopic.set(w.topic, arr);
    });

    const map = new Map<string, TopicMastery>();
    for (const t of topics) {
      const words = byTopic.get(t.id) || [];
      const total   = words.length;
      const correct = words.filter(w => w.correct).length;
      const pct     = total > 0 ? Math.round((correct / total) * 100) : 0;
      map.set(t.id, {
        topicId:   t.id,
        attempted: correct,
        correct,
        total,
        pct,
        done:      pct >= 80
      });
    }
    return map;
  }, { swr: true }) as Promise<Map<string, TopicMastery>>;
}

/** Local cache of the user's "current" topic. */
export function getRecentTopicId(): string | null {
  try { return localStorage.getItem(RECENT_TOPIC_KEY); } catch { return null; }
}
export function setRecentTopicId(topicId: string) {
  try { localStorage.setItem(RECENT_TOPIC_KEY, topicId); } catch {}
}

/**
 * Resolve the topic the user should land on when they hit a mode
 * without a ?topic= query param. Order of preference:
 *
 *   1. URL ?topic=  (caller checks this first)
 *   2. localStorage arabise-recent-topic, if still valid
 *   3. First topic in the list where mastery < 80%
 *   4. First topic overall
 */
export async function resolveStartTopic(topics: Topic[], mastery: Map<string, TopicMastery>): Promise<Topic | null> {
  if (topics.length === 0) return null;
  const recent = getRecentTopicId();
  if (recent) {
    const m = topics.find(t => t.id === recent);
    if (m) return m;
  }
  const firstUnfinished = topics.find(t => !mastery.get(t.id)?.done);
  if (firstUnfinished) return firstUnfinished;
  return topics[0];
}

/**
 * Given a topic id and the ordered topic list, return the one that
 * comes after it. Wraps to topics[0] at the end (user finished
 * everything — start the loop over).
 */
export function nextTopicAfter(currentId: string, topics: Topic[]): Topic | null {
  if (topics.length === 0) return null;
  const i = topics.findIndex(t => t.id === currentId);
  if (i < 0) return topics[0];
  return topics[(i + 1) % topics.length];
}

/* ───────────────── Vocab fetched for Speed Review / Revise / Assistance ───────────────── */

/**
 * Fetch every vocab word the user has ever attempted. Optionally
 * narrow to a single topic (Speed Review uses the recent-topic
 * scope; Revise & Assistance pull everything).
 */
export async function fetchAttemptedVocab(opts?: { topicId?: string }): Promise<VocabWord[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const progressRes = await supabase
    .from("progress")
    .select("word_id")
    .eq("user_id", user.id);
  if (progressRes.error) { console.warn("[queries] attempted progress", progressRes.error); return []; }

  // Unique word_ids — and strip any "s:<id>" sentence rows so we
  // only end up with vocab.
  const ids = Array.from(new Set(
    (progressRes.data || [])
      .map(r => String(r.word_id))
      .filter(id => !/^s:/i.test(id))
  ));
  if (ids.length === 0) return [];

  let q = supabase
    .from("vocab")
    .select("id, arabic, english, transliteration, topic")
    .in("id", ids);
  if (opts?.topicId) q = q.eq("topic", opts.topicId);

  const { data, error } = await q;
  if (error) { console.warn("[queries] attempted vocab", error); return []; }
  return data || [];
}

/**
 * Find words where the user's MOST RECENT progress row was wrong.
 * Used by Assistance mode.
 */
export async function fetchWrongWords(limit = 5): Promise<VocabWord[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const progressRes = await supabase
    .from("progress")
    .select("word_id, correct, answered_at")
    .eq("user_id", user.id);
  if (progressRes.error) { console.warn("[queries] wrong progress", progressRes.error); return []; }

  const latest = new Map<string, { correct: boolean; when: number }>();
  (progressRes.data || []).forEach(r => {
    const id = String(r.word_id);
    if (/^s:/i.test(id)) return; // skip sentence rows
    const when = r.answered_at ? new Date(r.answered_at).getTime() : 0;
    const prev = latest.get(id);
    if (!prev || when > prev.when) latest.set(id, { correct: !!r.correct, when });
  });

  const wrongIds = Array.from(latest.entries())
    .filter(([, v]) => !v.correct)
    .map(([id]) => id);
  if (wrongIds.length === 0) return [];

  const { data, error } = await supabase
    .from("vocab")
    .select("id, arabic, english, transliteration, topic")
    .in("id", wrongIds);
  if (error) { console.warn("[queries] wrong vocab", error); return []; }

  const shuffled = (data || []).sort(() => Math.random() - 0.5);
  return shuffled.slice(0, limit);
}

/* ───────────────── Onboarding helper ───────────────── */

export async function completeOnboarding() {
  try {
    const { error } = await supabase.rpc("complete_onboarding");
    if (!error) return;
  } catch { /* fall through */ }
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in.");
  const { error } = await supabase
    .from("profiles")
    .update({ onboarding_complete: true })
    .eq("id", user.id);
  if (error) throw error;
}
